import { useEffect, useState } from "react";
import { LOAD_TRANSLATE_EVENT } from "@/components/google-translate-init";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const STORAGE_KEY = "saased.languagePref.v1";
const DEFAULT_LANG = "en";

// Native name for each ISO 639-1 code (used in the prompt itself).
const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
  af: "Afrikaans", am: "አማርኛ", ar: "العربية", az: "Azərbaycanca", be: "Беларуская",
  bg: "Български", bn: "বাংলা", bs: "Bosanski", ca: "Català", cs: "Čeština",
  da: "Dansk", de: "Deutsch", el: "Ελληνικά", es: "Español", et: "Eesti",
  eu: "Euskara", fa: "فارسی", fi: "Suomi", fil: "Filipino", fr: "Français",
  ga: "Gaeilge", gl: "Galego", gu: "ગુજરાતી", he: "עברית", hi: "हिन्दी",
  hr: "Hrvatski", hu: "Magyar", hy: "Հայերեն", id: "Bahasa Indonesia", is: "Íslenska",
  it: "Italiano", ja: "日本語", ka: "ქართული", kk: "Қазақша", km: "ខ្មែរ",
  kn: "ಕನ್ನಡ", ko: "한국어", ky: "Кыргызча", lo: "ລາວ", lt: "Lietuvių",
  lv: "Latviešu", mk: "Македонски", ml: "മലയാളം", mn: "Монгол", mr: "मराठी",
  ms: "Bahasa Melayu", my: "မြန်မာ", ne: "नेपाली", nl: "Nederlands", no: "Norsk",
  pa: "ਪੰਜਾਬੀ", pl: "Polski", ps: "پښتو", pt: "Português", ro: "Română",
  ru: "Русский", si: "සිංහල", sk: "Slovenčina", sl: "Slovenščina", sq: "Shqip",
  sr: "Српски", sv: "Svenska", sw: "Kiswahili", ta: "தமிழ்", te: "తెలుగు",
  tg: "Тоҷикӣ", th: "ไทย", tk: "Türkmençe", tr: "Türkçe", uk: "Українська",
  ur: "اردو", uz: "Oʻzbekcha", vi: "Tiếng Việt", zh: "中文", zu: "isiZulu",
};

// English name for each ISO 639-1 code (used in the English subtitle so it
// never contains native non-English characters).
const ENGLISH_LANGUAGE_NAMES: Record<string, string> = {
  af: "Afrikaans", am: "Amharic", ar: "Arabic", az: "Azerbaijani", be: "Belarusian",
  bg: "Bulgarian", bn: "Bengali", bs: "Bosnian", ca: "Catalan", cs: "Czech",
  da: "Danish", de: "German", el: "Greek", es: "Spanish", et: "Estonian",
  eu: "Basque", fa: "Persian", fi: "Finnish", fil: "Filipino", fr: "French",
  ga: "Irish", gl: "Galician", gu: "Gujarati", he: "Hebrew", hi: "Hindi",
  hr: "Croatian", hu: "Hungarian", hy: "Armenian", id: "Indonesian", is: "Icelandic",
  it: "Italian", ja: "Japanese", ka: "Georgian", kk: "Kazakh", km: "Khmer",
  kn: "Kannada", ko: "Korean", ky: "Kyrgyz", lo: "Lao", lt: "Lithuanian",
  lv: "Latvian", mk: "Macedonian", ml: "Malayalam", mn: "Mongolian", mr: "Marathi",
  ms: "Malay", my: "Burmese", ne: "Nepali", nl: "Dutch", no: "Norwegian",
  pa: "Punjabi", pl: "Polish", ps: "Pashto", pt: "Portuguese", ro: "Romanian",
  ru: "Russian", si: "Sinhala", sk: "Slovak", sl: "Slovenian", sq: "Albanian",
  sr: "Serbian", sv: "Swedish", sw: "Swahili", ta: "Tamil", te: "Telugu",
  tg: "Tajik", th: "Thai", tk: "Turkmen", tr: "Turkish", uk: "Ukrainian",
  ur: "Urdu", uz: "Uzbek", vi: "Vietnamese", zh: "Chinese", zu: "Zulu",
};


// Translated copy for the dialog. Falls back to English if a code is missing —
// the {lang} placeholder is the native language name so the question still
// reads naturally for any locale.
const COPY: Record<string, { title: string; yes: string; no: string }> = {
  en: { title: "Would you like to view the website in {lang}?", yes: "Yes, switch language", no: "No, thanks" },
  fr: { title: "Souhaitez-vous consulter le site en {lang} ?", yes: "Oui, changer de langue", no: "Non, merci" },
  de: { title: "Möchten Sie die Website auf {lang} anzeigen?", yes: "Ja, Sprache wechseln", no: "Nein, danke" },
  es: { title: "¿Quieres ver el sitio web en {lang}?", yes: "Sí, cambiar idioma", no: "No, gracias" },
  it: { title: "Vuoi visualizzare il sito in {lang}?", yes: "Sì, cambia lingua", no: "No, grazie" },
  pt: { title: "Quer ver o site em {lang}?", yes: "Sim, mudar idioma", no: "Não, obrigado" },
  nl: { title: "Wil je de website in het {lang} bekijken?", yes: "Ja, taal wijzigen", no: "Nee, bedankt" },
  pl: { title: "Czy chcesz wyświetlić stronę w języku {lang}?", yes: "Tak, zmień język", no: "Nie, dziękuję" },
  ru: { title: "Хотите просмотреть сайт на {lang}?", yes: "Да, сменить язык", no: "Нет, спасибо" },
  uk: { title: "Бажаєте переглянути сайт мовою {lang}?", yes: "Так, змінити мову", no: "Ні, дякую" },
  tr: { title: "Web sitesini {lang} dilinde görüntülemek ister misiniz?", yes: "Evet, dili değiştir", no: "Hayır, teşekkürler" },
  ar: { title: "هل تريد عرض الموقع باللغة {lang}؟", yes: "نعم، غيّر اللغة", no: "لا، شكراً" },
  he: { title: "האם תרצה לצפות באתר ב{lang}?", yes: "כן, החלף שפה", no: "לא, תודה" },
  fa: { title: "آیا می‌خواهید سایت را به {lang} مشاهده کنید؟", yes: "بله، تغییر زبان", no: "خیر، متشکرم" },
  ja: { title: "ウェブサイトを{lang}で表示しますか？", yes: "はい、切り替える", no: "いいえ、結構です" },
  ko: { title: "웹사이트를 {lang}로 보시겠습니까?", yes: "예, 언어 변경", no: "아니요, 괜찮습니다" },
  zh: { title: "您想以{lang}查看本网站吗？", yes: "是的，切换语言", no: "不用了，谢谢" },
  hi: { title: "क्या आप वेबसाइट को {lang} में देखना चाहते हैं?", yes: "हाँ, भाषा बदलें", no: "नहीं, धन्यवाद" },
  bn: { title: "আপনি কি ওয়েবসাইটটি {lang}-এ দেখতে চান?", yes: "হ্যাঁ, ভাষা পরিবর্তন করুন", no: "না, ধন্যবাদ" },
  th: { title: "คุณต้องการดูเว็บไซต์เป็นภาษา{lang}หรือไม่?", yes: "ใช่ เปลี่ยนภาษา", no: "ไม่ ขอบคุณ" },
  vi: { title: "Bạn có muốn xem trang web bằng tiếng {lang} không?", yes: "Có, đổi ngôn ngữ", no: "Không, cảm ơn" },
  id: { title: "Apakah Anda ingin melihat situs web dalam bahasa {lang}?", yes: "Ya, ganti bahasa", no: "Tidak, terima kasih" },
  sv: { title: "Vill du visa webbplatsen på {lang}?", yes: "Ja, byt språk", no: "Nej, tack" },
  no: { title: "Vil du se nettstedet på {lang}?", yes: "Ja, bytt språk", no: "Nei takk" },
  da: { title: "Vil du se webstedet på {lang}?", yes: "Ja, skift sprog", no: "Nej tak" },
  fi: { title: "Haluatko katsoa sivuston kielellä {lang}?", yes: "Kyllä, vaihda kieli", no: "Ei kiitos" },
  cs: { title: "Chcete zobrazit web v jazyce {lang}?", yes: "Ano, změnit jazyk", no: "Ne, děkuji" },
  el: { title: "Θέλετε να δείτε τον ιστότοπο στα {lang};", yes: "Ναι, αλλαγή γλώσσας", no: "Όχι, ευχαριστώ" },
  ro: { title: "Doriți să vizualizați site-ul în {lang}?", yes: "Da, schimbă limba", no: "Nu, mulțumesc" },
  hu: { title: "Szeretné a webhelyet {lang} nyelven megtekinteni?", yes: "Igen, váltás", no: "Nem, köszönöm" },
};

function detectBrowserLanguage(): string | null {
  try {
    const nav = navigator as Navigator & { languages?: readonly string[] };
    const raw = (nav.languages?.[0] ?? nav.language ?? "")
      .split("-")[0]
      ?.toLowerCase()
      .trim();
    return raw && raw.length >= 2 ? raw : null;
  } catch {
    return null;
  }
}

export function LanguageSuggestionDialog() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }

    // Purely local detection — no network request, so no request can ever fail
    // or emit a console error (previously ipapi.co could return 429).
    try {
      const detected = detectBrowserLanguage();
      if (!detected || detected === DEFAULT_LANG) return;
      setLang(detected);
      setOpen(true);
    } catch {
      // any failure resolves silently to the default language
    }
  }, []);


  const savePref = (pref: "switched" | "dismissed") => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ pref, lang, at: Date.now() }));
    } catch {
      // storage unavailable — proceed without persisting
    }
    setOpen(false);
  };

  const handleYes = () => {
    if (lang && typeof window !== "undefined") {
      // The Google Translate widget is only loaded for visitors who opt in.
      window.dispatchEvent(new Event(LOAD_TRANSLATE_EVENT));

      const trigger = () => {
        const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
        if (!combo) return false;
        combo.value = lang;
        combo.dispatchEvent(new Event("change"));
        return true;
      };

      if (!trigger()) {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (trigger() || attempts > 33) {
            clearInterval(interval);
          }
        }, 300);
      }
    }
    savePref("switched");
  };

  if (!lang) return null;

  const nativeName = NATIVE_LANGUAGE_NAMES[lang] ?? lang.toUpperCase();
  const englishName = ENGLISH_LANGUAGE_NAMES[lang] ?? lang.toUpperCase();
  const copy = COPY[lang] ?? COPY.en;
  const title = copy.title.replace("{lang}", nativeName);
  const englishSubtitle = `Would you like to view the website in ${englishName}?`;
  const isRtl = ["ar", "he", "fa", "ur", "ps"].includes(lang);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) savePref("dismissed"); }}>
      <DialogContent dir={isRtl ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{englishSubtitle}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            onClick={() => savePref("dismissed")}
            className="rounded-full border border-border bg-background px-5 py-2 font-sans text-sm font-medium uppercase tracking-[0.15em] text-foreground"
          >
            {copy.no}
          </button>
          <button
            onClick={handleYes}
            className="rounded-full bg-primary px-5 py-2 font-sans text-sm font-medium uppercase tracking-[0.15em] text-primary-foreground"
          >
            {copy.yes}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
